import { useEffect, useState } from "react";
import { Ban, Inbox, Send, StickyNote, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { THREAD_STATUS_LABELS, TOPIC_LABELS } from "@/lib/publicApi";
import { Loading } from "@/components/common";
import { Textarea } from "@/components/ui/textarea";
import { Action, EmptyState, PageHeader, Panel, SelectField, StatusBadge, Toolbar } from "./ui";

const TONES = {
  received: "pending",
  in_analysis: "progress",
  answered: "done",
  waiting_for_user: "progress",
  closed: "neutral",
  blocked_as_abuse: "alert",
};

export default function AnonymousInbox() {
  const { api } = useAuth();
  const [threads, setThreads] = useState(null);
  const [filter, setFilter] = useState({ status: "", topic: "", unassigned: false });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/portal/anonymous-threads", {
        params: {
          status: filter.status || undefined,
          topic: filter.topic || undefined,
          unassigned: filter.unassigned || undefined,
        },
      });
      setThreads(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setThreads([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.status, filter.topic, filter.unassigned]);

  const open = async (thread) => {
    try {
      const { data } = await api.get(`/portal/anonymous-threads/${thread.id}`);
      setSelected(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const run = async (fn, message) => {
    try {
      await fn();
      if (message) toast.success(message);
      await load();
      if (selected) await open(selected.thread);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (threads === null) return <Loading label="Carregando caixa de entrada…" />;

  return (
    <div data-testid="portal-anonymous-page" className="space-y-6">
      <PageHeader
        kicker="Canal sem identificação"
        title="Perguntas anônimas"
        subtitle="Respostas informativas: sem diagnóstico, sem prescrição e sem substituir avaliação profissional. Notas internas nunca são exibidas para quem enviou a pergunta."
      />

      <Toolbar testId="threads-toolbar">
        <SelectField
          testId="threads-filter-status"
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">Todos os status</option>
          {Object.entries(THREAD_STATUS_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </SelectField>
        <SelectField
          testId="threads-filter-topic"
          value={filter.topic}
          onChange={(e) => setFilter({ ...filter, topic: e.target.value })}
        >
          <option value="">Todos os temas</option>
          {Object.entries(TOPIC_LABELS).map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </SelectField>
        <Action
          variant={filter.unassigned ? "primary" : "secondary"}
          data-testid="threads-filter-unassigned"
          onClick={() => setFilter({ ...filter, unassigned: !filter.unassigned })}
        >
          Fila de não atribuídas
        </Action>
      </Toolbar>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <div className="space-y-3">
          {threads.length === 0 ? (
            <EmptyState
              testId="threads-empty"
              icon={Inbox}
              title="Nenhuma pergunta com esses filtros"
              description="Ajuste os filtros acima para ver as demais perguntas do canal anônimo."
            />
          ) : (
            threads.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`thread-row-${t.id}`}
                onClick={() => open(t)}
                className={`w-full rounded-2xl border bg-white p-5 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:bg-sand/40 ${
                  selected?.thread?.id === t.id ? "border-sage-600" : "border-border"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[13px] text-ink-500">#{t.id}</span>
                  <StatusBadge tone={TONES[t.status]} testId={`thread-status-${t.id}`}>
                    {THREAD_STATUS_LABELS[t.status] || t.status}
                  </StatusBadge>
                </div>
                <p className="mt-2.5 text-[15px] font-semibold text-ink-900">{TOPIC_LABELS[t.topic] || t.topic}</p>
                <p className="mt-1.5 text-[13.5px] text-ink-500">
                  {t.message_count} mensagem(ns) · {formatDate(t.created_at)}
                  {t.assigned_name ? ` · ${t.assigned_name}` : " · não atribuída"}
                </p>
              </button>
            ))
          )}
        </div>

        {selected ? (
          <Panel testId="thread-detail" className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-mono text-[14px] text-ink-600">#{selected.thread.id}</p>
                <p className="mt-0.5 text-[13.5px] text-ink-500">{TOPIC_LABELS[selected.thread.topic]}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Action
                  size="sm"
                  variant="secondary"
                  icon={UserCheck}
                  data-testid="thread-assign"
                  onClick={() =>
                    run(() => api.post(`/portal/anonymous-threads/${selected.thread.id}/assign`), "Atribuída a você")
                  }
                >
                  Atribuir a mim
                </Action>
                <Action
                  size="sm"
                  variant="secondary"
                  data-testid="thread-close"
                  onClick={() =>
                    run(
                      () => api.post(`/portal/anonymous-threads/${selected.thread.id}/status`, { status: "closed" }),
                      "Conversa encerrada"
                    )
                  }
                >
                  Encerrar
                </Action>
                <Action
                  size="sm"
                  variant="danger"
                  icon={Ban}
                  data-testid="thread-block"
                  onClick={() =>
                    run(
                      () =>
                        api.post(`/portal/anonymous-threads/${selected.thread.id}/status`, {
                          status: "blocked_as_abuse",
                        }),
                      "Bloqueada por abuso"
                    )
                  }
                >
                  Abuso
                </Action>
              </div>
            </div>

            <div className="space-y-3">
              {selected.messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`thread-message-${m.id}`}
                  className={`rounded-2xl p-4 ${m.author === "user" ? "bg-sand/70" : "border border-border bg-white"}`}
                >
                  <p className="er-label text-[12px] tracking-[0.16em]">
                    {m.author === "user" ? "Pessoa (anônima)" : "Erimus"}
                  </p>
                  <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-ink-700">{m.text}</p>
                  <p className="mt-2 text-[13px] text-ink-500">
                    {formatDate(m.created_at)}
                    {m.author === "erimus" ? (m.read_by_user ? " · lida" : " · não lida") : ""}
                  </p>
                </div>
              ))}
            </div>

            <div className="space-y-3 border-t border-border pt-5">
              <Textarea
                data-testid="thread-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={4}
                placeholder="Resposta informativa, sem diagnóstico ou prescrição"
                className="rounded-xl text-[14.5px]"
              />
              <Action
                icon={Send}
                data-testid="thread-reply-submit"
                disabled={reply.trim().length < 5}
                onClick={() =>
                  run(
                    () => api.post(`/portal/anonymous-threads/${selected.thread.id}/reply`, { message: reply }),
                    "Resposta enviada"
                  ).then(() => setReply(""))
                }
              >
                Responder
              </Action>
            </div>

            <div className="space-y-3 border-t border-border pt-5">
              <p className="er-label flex items-center gap-2 text-[12px] tracking-[0.16em]">
                <StickyNote size={14} /> Notas internas (nunca visíveis para o usuário)
              </p>
              {selected.internal_notes.map((n) => (
                <p
                  key={n.id}
                  data-testid={`thread-note-${n.id}`}
                  className="rounded-xl bg-warning/12 p-3.5 text-[14px] leading-relaxed text-ink-700"
                >
                  {n.text}{" "}
                  <span className="text-ink-500">
                    · {n.author_name || "equipe"} · {formatDate(n.created_at)}
                  </span>
                </p>
              ))}
              <Textarea
                data-testid="thread-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Registrar nota interna"
                className="rounded-xl text-[14.5px]"
              />
              <Action
                variant="secondary"
                data-testid="thread-note-submit"
                disabled={note.trim().length < 2}
                onClick={() =>
                  run(() => api.post(`/portal/anonymous-threads/${selected.thread.id}/notes`, { note }), "Nota registrada").then(
                    () => setNote("")
                  )
                }
              >
                Adicionar nota
              </Action>
            </div>
          </Panel>
        ) : (
          <EmptyState
            testId="thread-none-selected"
            icon={Inbox}
            title="Selecione uma pergunta"
            description="Escolha uma pergunta na lista para ver a conversa e responder."
          />
        )}
      </div>
    </div>
  );
}
